import { isApiClient, isApiRestrictedClient } from '../../shared/helpers/client-type.func';
import { formatDateToISOString, mapDateOrNull } from '../../shared/helpers/date.func';
import {
	ApiClient,
	ApiRestrictedClient,
	ApiUserClient,
	CreateApiClientBody,
	UpdateApiClientBody,
} from '../api-types/client.api';
import { Client, CreateClientBody, RestrictedClient, UpdateClient, UserClient } from '../app-types/client.app';
import {
	transformApiAddressToAppAddress,
	transformAppAddressToApiAddress,
	transformAppCreateAddressToApiCreateAddressBody,
} from './address.transform';
import { transformApiUserClientAssignmentsToAppUserClientAssignments } from './assignment.transform';
import {
	transformApiContactPointsToAppContactPoints,
	transformAppContactPointToApiContactPoint,
} from './contact-point.transform';
import { transformApiGenderToAppGender, transformAppGenderToApiGender } from './gender.transform';
import { removeWhitespaceAndNewlines } from './helpers';
import {
	transformApiNationalityToAppNationality,
	transformAppNationalityToApiNationality,
} from './nationality.transform';

/**
 * Transforms an ApiClient object to an app-specific Client object.
 * @param input ApiClient
 * @description Transforms an ApiClient object to an app-specific Client object.
 * This function maps the properties of the ApiClient to the Client interface.
 * It is used to convert data received from the API into a format that the application can work with.
 * @returns Client
 */
export const transformApiClientToAppClient = (input: ApiClient) => {
	const output: Client = {
		id: input.id,
		name: removeWhitespaceAndNewlines(input.name),
		firstName: removeWhitespaceAndNewlines(input.firstName),
		dateOfBirth: mapDateOrNull(input.dateOfBirth) ?? undefined,
		birthName: removeWhitespaceAndNewlines(input.birthName),
		title: removeWhitespaceAndNewlines(input.title),
		gender: input.gender ? transformApiGenderToAppGender(input.gender) : undefined,
		residenceDistrict: input.countyId,
		nationality: transformApiNationalityToAppNationality(input.nationality),
		numberOfChildren: input.numberOfChildren,
		numberSV: input.numberSV,
		numberBA: input.numberBA,
		numberKV: input.numberKV,
		numberW: input.numberW,
		otherTextIndustry: input.otherTextIndustry,
		specialSchool: input.specialSchool,
		remark: input.remark,
		department: input.department,
		role: input.role,
		soundex: input.soundex,
		otherText: input.otherText,
		deactivationReason: input.deactivationReason,
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoints || []),
		uuid: input.guid,
		archiveId: input.archiveId,
	};
	if (input.address) output.address = transformApiAddressToAppAddress(input.address);
	return output;
};

export const transformAppClientToApiClient = (input: Client) => {
	const output: ApiClient = {
		id: input.id,
		name: removeWhitespaceAndNewlines(input.name),
		firstName: removeWhitespaceAndNewlines(input.firstName),
		dateOfBirth: input.dateOfBirth?.getISODateStringWithoutTime(),
		birthName: removeWhitespaceAndNewlines(input.birthName),
		title: removeWhitespaceAndNewlines(input.title),
		gender: input.gender ? transformAppGenderToApiGender(input.gender) : undefined,
		countyId: input.residenceDistrict,
		nationality: transformAppNationalityToApiNationality(input.nationality),
		numberOfChildren: input.numberOfChildren,
		numberSV: input.numberSV,
		numberBA: input.numberBA,
		numberKV: input.numberKV,
		numberW: input.numberW,
		otherTextIndustry: input.otherTextIndustry,
		specialSchool: input.specialSchool,
		remark: input.remark,
		department: input.department,
		role: input.role,
		soundex: input.soundex,
		otherText: input.otherText,
		deactivationReason: input.deactivationReason,
		contactPoints: (input.contactPoints || []).map(transformAppContactPointToApiContactPoint),
		guid: input.uuid,
		address: input.address
			? transformAppAddressToApiAddress(input.address)
			: {
					anonym: '',
					streetAndHouseNumber: '',
					postalCodeAndCity: '',
					soundexStreet: '',
				},
		archiveId: input.archiveId,
	};

	return output;
};

/**
 * Transforms an array of ApiClient objects to an array of app-specific Client objects.
 * @param input Array of ApiClient objects
 * @description Transforms an array of ApiClient objects to an array of app-specific Client objects.
 * This function is useful when you need to convert multiple clients received from the API
 * into a format that the application can work with. It uses the `transformApiClientToAppClient`
 * function to transform each individual client.
 * @returns Array of Client objects
 */
export const transformApiClientsToAppClients = (input: ApiClient[]) => {
	return input.map(transformApiClientToAppClient);
};

export const transformApiRestrictedClientToAppRestrictedClient = (input: ApiRestrictedClient) => {
	const output: RestrictedClient = {
		id: input.id,
		objectId: input.objectId,
		firstName: input.firstName,
		name: input.name,
		dateOfBirth: mapDateOrNull(input.dateOfBirth) ?? undefined,
		consultant: input.consultant,
	};
	return output;
};

export const transformCreateClientToCreateApiClientBody = (input: CreateClientBody) => {
	const dateOfBirth = formatDateToISOString(input.dateOfBirth);
	const output: CreateApiClientBody = {
		name: input.name,
		firstName: input.firstName,
		dateOfBirth: dateOfBirth,
		birthName: input.birthName,
		title: input.title,
		address: transformAppCreateAddressToApiCreateAddressBody(input.address),
		gender: input.gender ? transformAppGenderToApiGender(input.gender) : undefined,
		nationality: transformAppNationalityToApiNationality(input.nationality),
		numberOfChildren: input.numberOfChildren,
		numberSV: input.numberSV,
		numberBA: input.numberBA,
		numberKV: input.numberKV,
		numberW: input.numberW,
		otherTextIndustry: input.otherTextIndustry,
		specialSchool: input.specialSchool,
		remark: input.remark,
		department: input.department,
		role: input.role,
		soundex: input.soundex,
		otherText: input.otherText,
		deactivationReason: input.deactivationReason,
		contactPoints: input.communicationWays?.map((cw) => ({
			categoryContactPointId: cw.categoryContactPointId,
			contactValue: cw.contactValue,
			preferredWayOfContact: cw.preferredWayOfContact,
			remark: cw.remark,
		})),
	};
	if (input.residenceDistrict) output.countyId = input.residenceDistrict;
	return output;
};

export const transformUpdateClientToUpdateClientBody = (input: UpdateClient) => {
	const dateOfBirth = formatDateToISOString(input.dateOfBirth);
	const output: UpdateApiClientBody = {
		address: transformAppAddressToApiAddress(input.address),
		numberOfChildren: input.numberOfChildren,
		numberSV: input.numberSV,
		numberBA: input.numberBA,
		numberKV: input.numberKV,
		numberW: input.numberW,
		otherTextIndustry: input.otherTextIndustry,
		specialSchool: input.specialSchool,
		contactPoints: input.communicationWays?.map((cw) => ({
			categoryContactPointId: cw.categoryContactPointId,
			contactValue: cw.contactValue,
			preferredWayOfContact: cw.preferredWayOfContact,
			remark: cw.remark,
		})),
		firstName: input.firstName,
		dateOfBirth: dateOfBirth,
		title: input.title,
		remark: input.remark,
		birthName: input.birthName,
		department: input.department,
		role: input.role,
		gender: input.gender ? transformAppGenderToApiGender(input.gender) : undefined,
		name: input.name,
		soundex: input.soundex,
		otherText: input.otherText,
		deactivationReason: input.deactivationReason,
		nationality: transformAppNationalityToApiNationality(input.nationality),
	};
	if (input.residenceDistrict) output.countyId = input.residenceDistrict;
	return output;
};

export const transformAnyApiClientToAppClient = (input: ApiClient | ApiRestrictedClient) => {
	if (isApiClient(input)) {
		return transformApiClientToAppClient(input);
	}

	if (isApiRestrictedClient(input)) {
		return transformApiRestrictedClientToAppRestrictedClient(input);
	}

	return undefined;
};

export const transformApiUserClientToAppUserClient = (input: ApiUserClient) => {
	const output: UserClient = {
		id: input.id,
		clientFullName: input.clientFullName,
		assignments: input.assignments
			? transformApiUserClientAssignmentsToAppUserClientAssignments(input.assignments)
			: [],
	};
	return output;
};
